import i18next, { type i18n, type TFunction } from "i18next";
import { COPY_GROUPS, type CopyGroupId } from "./copy-lazy.gen.ts";
import { registerFormatters } from "./formatters.ts";
import { DEFAULT_LOCALE, type LiveLocale, LOCALES } from "./locales.ts";
import { GROUP_OF_KEY } from "./paths.ts";

/**
 * The browser's way into the copy. `i18n.ts` bundles every feature and language, which is right for the server and for mail;
 * the web app takes `createWebI18n` instead and fetches the language the person reads, once, when it is first
 * needed. Nothing here imports `copy/` statically, so a screen never carries copy for screens it isn't showing.
 */
export {
  createFormat,
  type DateInput,
  type DateStyle,
  type FormatOptions,
  type Formatter,
} from "./format.ts";
export {
  DEFAULT_LOCALE,
  INTL_TAGS,
  intlTag,
  isLiveLocale,
  isLocale,
  LIVE_LOCALES,
  type LiveLocale,
  LOCALE_NAMES,
  LOCALES,
  type Locale,
} from "./locales.ts";
export { groupsForPath } from "./paths.ts";
export { localeFromAcceptLanguage, resolveLocale } from "./resolve.ts";
export type { PartialTranslation, Translation } from "./types.ts";

export const NAMESPACE = "app";
export type AdeaT = TFunction<"app">;

/** What every screen needs at once, per language (each is its own chunk). */
const BASE: Record<LiveLocale, () => Promise<object>> = {
  en: () => import("./copy-base.gen.ts").then((m) => m.BASE_COPY.en),
  da: () => import("./copy-base.gen.ts").then((m) => m.BASE_COPY.da),
};

/** Who is waiting on what, per instance: `${locale}:${group}` (or `${locale}:*` for the base). */
const fetched = new WeakMap<i18n, Map<string, Promise<void>>>();
const arrived = new WeakMap<i18n, Set<string>>();

function memo(instance: i18n, name: string, fetch: () => Promise<object>, locale: LiveLocale): Promise<void> {
  let map = fetched.get(instance);
  if (!map) {
    map = new Map();
    fetched.set(instance, map);
  }
  let promise = map.get(name);
  if (!promise) {
    promise = fetch().then((bundle) => {
      instance.addResourceBundle(locale, NAMESPACE, bundle, true, true);
      let set = arrived.get(instance);
      if (!set) {
        set = new Set();
        arrived.set(instance, set);
      }
      set.add(name);
    });
    map.set(name, promise);
  }
  return promise;
}

const baseName = (locale: LiveLocale) => `${locale}:*`;
const groupName = (locale: LiveLocale, id: CopyGroupId) => `${locale}:${id}`;

/** An instance with no copy yet: `loadCopy` adds a language, `loadGroups` the screens' pieces of it. */
export function createWebI18n(
  locale: LiveLocale = DEFAULT_LOCALE,
  options: {
    timeZone?: string;
    /** Told when a key is read before its piece arrived (it reads as nothing until then): a development warning. */
    onLateKey?: (key: string, group: CopyGroupId) => void;
  } = {},
): i18n {
  const instance = i18next.createInstance();
  void instance.init({
    lng: locale,
    supportedLngs: [...LOCALES],
    fallbackLng: { default: [DEFAULT_LOCALE] },
    ns: [NAMESPACE],
    defaultNS: NAMESPACE,
    resources: {},
    initAsync: false,
    returnNull: false,
    interpolation: { escapeValue: false },
    // A key whose piece hasn't arrived yet reads as nothing (never as its name) while the piece is fetched; the app
    // draws again when it lands (the provider listens for `added`). A key that is in no piece is a mistake: its name.
    parseMissingKeyHandler: (key: string, defaultValue?: string, missing?: { lng?: string }) =>
      wantKey(instance, key, missing?.lng, options.onLateKey) ? "" : (defaultValue ?? key),
  });
  registerFormatters(instance, options);
  return instance;
}

/** The language the app is being read in right now (the pieces missing from it are the ones to fetch). */
const reading = new WeakMap<i18n, LiveLocale>();
export const readIn = (instance: i18n, locale: LiveLocale): void => void reading.set(instance, locale);

/** Has this language's base copy arrived? */
export const copyLoaded = (instance: i18n, locale: LiveLocale): boolean =>
  arrived.get(instance)?.has(baseName(locale)) === true;

/** Fetches a language's base copy (once, however often it is asked). */
export function loadCopy(instance: i18n, locale: LiveLocale): Promise<void> {
  return memo(instance, baseName(locale), BASE[locale], locale);
}

/** Fetches the pieces of copy for these groups, in this language. */
export function loadGroups(instance: i18n, locale: LiveLocale, ids: Iterable<CopyGroupId>): Promise<void> {
  const all: Promise<void>[] = [];
  for (const id of ids)
    all.push(
      memo(
        instance,
        groupName(locale, id),
        () => COPY_GROUPS[id].load().then((m) => m.default[locale]),
        locale,
      ),
    );
  return Promise.all(all).then(() => undefined);
}

/**
 * The pieces asked for in another language and not in this one. A key whose piece is missing in the reader's language
 * but there in English reads in English (the fallback), and asks for nothing: after a language switch, or when the
 * profile's language isn't the browser's, the screens already opened would stay English.
 */
function behind(instance: i18n, locale: LiveLocale): CopyGroupId[] {
  const asked = fetched.get(instance);
  if (!asked) return [];
  const out = new Set<CopyGroupId>();
  for (const name of asked.keys()) {
    const [lng, id] = name.split(":") as [string, string];
    if (lng !== locale && id !== "*" && id in COPY_GROUPS && !asked.has(groupName(locale, id as CopyGroupId)))
      out.add(id as CopyGroupId);
  }
  return [...out];
}

/** Has this language every piece another language has been asked for? */
export const caughtUp = (instance: i18n, locale: LiveLocale): boolean =>
  behind(instance, locale).length === 0;

/** Fetches, in this language, the pieces asked for in another one (see `behind`). */
export function catchUp(instance: i18n, locale: LiveLocale): Promise<void> {
  return loadGroups(instance, locale, behind(instance, locale));
}

const everything = new WeakMap<i18n, Map<LiveLocale, Promise<void>>>();

/** Every piece, for places that show any screen (the lab, a print). The same promise each time it is asked. */
export function loadAllCopy(instance: i18n, locale: LiveLocale): Promise<void> {
  let byLocale = everything.get(instance);
  if (!byLocale) {
    byLocale = new Map();
    everything.set(instance, byLocale);
  }
  let promise = byLocale.get(locale);
  if (!promise) {
    promise = Promise.all([
      loadCopy(instance, locale),
      loadGroups(instance, locale, Object.keys(COPY_GROUPS) as CopyGroupId[]),
    ]).then(() => undefined);
    byLocale.set(locale, promise);
  }
  return promise;
}

/** Has every piece of this language arrived? */
export const allCopyLoaded = (instance: i18n, locale: LiveLocale): boolean =>
  copyLoaded(instance, locale) &&
  (Object.keys(COPY_GROUPS) as CopyGroupId[]).every((id) =>
    arrived.get(instance)?.has(groupName(locale, id)),
  );

/**
 * A key was read before its piece arrived: start fetching it. True while that is the reason it reads as nothing.
 * (The reader is the `parseMissingKeyHandler` above, so this runs inside `t`.)
 */
function wantKey(
  instance: i18n,
  key: string,
  lng?: string,
  onLate?: (key: string, group: CopyGroupId) => void,
): boolean {
  const id = GROUP_OF_KEY.get(key.split(/[.:]/)[0] ?? "");
  const locale =
    (lng as LiveLocale | undefined) ?? reading.get(instance) ?? (instance.language as LiveLocale);
  if (!id || !(locale in BASE)) return false;
  if (arrived.get(instance)?.has(groupName(locale, id))) return false;
  onLate?.(key, id);
  void loadGroups(instance, locale, [id]);
  return true;
}
